'use client'

import Link from 'next/link'
import { Field, Button, Text } from '@/design-system'
import { AuthErrorMessage } from './AuthErrorMessage'
import { GoogleButton } from './GoogleButton'
import { VerificationStep } from './VerificationStep'
import { useSignInForm } from '@/hooks/auth/useSignInForm'

export function SignInForm() {
  const { email, password, isLoading, error, step, setField, handleSubmit, submitCode, resendCode, goBack } =
    useSignInForm()

  if (step === 'verification' || step === 'mfa') {
    return (
      <VerificationStep
        email={email}
        isLoading={isLoading}
        error={error}
        onSubmit={submitCode}
        onResend={resendCode}
        onBack={goBack}
      />
    )
  }

  const emailError = error === 'form_identifier_not_found'
  const passwordError = error === 'form_password_incorrect'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <GoogleButton />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          margin: '4px 0'
        }}>
        <div style={{ flex: 1, height: 2, background: 'var(--ink)', opacity: 0.15 }} />
        <Text as="span" variant="overline" opacity={0.6}>
          or
        </Text>
        <div style={{ flex: 1, height: 2, background: 'var(--ink)', opacity: 0.15 }} />
      </div>

      <div>
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(val) => setField('email', val)}
          placeholder="you@example.com"
          autoComplete="email"
        />
        {emailError && (
          <div style={{ marginTop: 6 }}>
            <AuthErrorMessage code={error} />
          </div>
        )}
      </div>

      <div>
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={(val) => setField('password', val)}
          placeholder="••••••••"
          autoComplete="current-password"
        />
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
          <Link href="/sign-in" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>
            <Text as="span" variant="tag">
              Forgot?
            </Text>
          </Link>
        </div>
        {passwordError && (
          <div style={{ marginTop: 6 }}>
            <AuthErrorMessage code={error} />
          </div>
        )}
      </div>

      {error && !emailError && !passwordError && (
        <div style={{ textAlign: 'center' }}>
          <AuthErrorMessage code={error} />
        </div>
      )}

      <div style={{ marginTop: 4 }}>
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          style={{ width: '100%', justifyContent: 'center' }}>
          {isLoading ? 'Signing in…' : 'Sign in →'}
        </Button>
      </div>

      {/* Clerk renders its Smart CAPTCHA (Cloudflare Turnstile) into this element
          when bot protection is enabled. Without it the SDK falls back to an
          invisible about:srcdoc CAPTCHA whose inline script our CSP frame-src
          blocks → sign-in 422s on production (works locally where the CSP/bot
          protection is lenient). Same fix as AcceptInviteForm. */}
      <div id="clerk-captcha" />
    </form>
  )
}
